"use strict";
/**
 * Coordinate reference systems for imported geodata.
 *
 * GeoJSON per RFC 7946 is always WGS84, but exports from German survey/GIS tools
 * routinely come in ETRS89 / UTM zone 32N (EPSG:25832) — the standard grid for
 * most of Germany — with the legacy `crs` member, or with none at all.
 *
 * The inverse transverse-Mercator series below is Snyder's, accurate to a few
 * centimetres well beyond the zone, which is far finer than the data warrants.
 * A projection library would cover more systems; this covers the one we need
 * without adding a dependency.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.IMPORT_CRS_OPTIONS = void 0;
exports.utm32ToWgs84 = utm32ToWgs84;
exports.reprojectGeoJson = reprojectGeoJson;
exports.detectImportCrs = detectImportCrs;
exports.IMPORT_CRS_OPTIONS = [
    { value: 'EPSG:4326', label: 'WGS 84 (Grad, EPSG:4326)' },
    { value: 'EPSG:25832', label: 'ETRS89 / UTM Zone 32N (EPSG:25832)' },
];
// GRS80 — ETRS89's ellipsoid. WGS84's differs in flattening by ~1e-11, which is
// well under a millimetre here, so the two are treated as one.
const A = 6378137.0;
const F = 1 / 298.257222101;
const E2 = F * (2 - F);
const K0 = 0.9996;
const FALSE_EASTING = 500000;
const ZONE_32_CENTRAL_MERIDIAN = 9 * Math.PI / 180;
/** ETRS89 / UTM 32N easting+northing → WGS84 [lng, lat]. */
function utm32ToWgs84(easting, northing) {
    const x = easting - FALSE_EASTING;
    const y = northing;
    const ep2 = E2 / (1 - E2);
    const m = y / K0;
    const mu = m / (A * (1 - E2 / 4 - 3 * E2 ** 2 / 64 - 5 * E2 ** 3 / 256));
    const e1 = (1 - Math.sqrt(1 - E2)) / (1 + Math.sqrt(1 - E2));
    const phi1 = mu
        + (3 * e1 / 2 - 27 * e1 ** 3 / 32) * Math.sin(2 * mu)
        + (21 * e1 ** 2 / 16 - 55 * e1 ** 4 / 32) * Math.sin(4 * mu)
        + (151 * e1 ** 3 / 96) * Math.sin(6 * mu)
        + (1097 * e1 ** 4 / 512) * Math.sin(8 * mu);
    const sinPhi1 = Math.sin(phi1);
    const cosPhi1 = Math.cos(phi1);
    const tanPhi1 = Math.tan(phi1);
    const c1 = ep2 * cosPhi1 ** 2;
    const t1 = tanPhi1 ** 2;
    const n1 = A / Math.sqrt(1 - E2 * sinPhi1 ** 2);
    const r1 = A * (1 - E2) / (1 - E2 * sinPhi1 ** 2) ** 1.5;
    const d = x / (n1 * K0);
    const lat = phi1 - (n1 * tanPhi1 / r1) * (d ** 2 / 2
        - (5 + 3 * t1 + 10 * c1 - 4 * c1 ** 2 - 9 * ep2) * d ** 4 / 24
        + (61 + 90 * t1 + 298 * c1 + 45 * t1 ** 2 - 252 * ep2 - 3 * c1 ** 2) * d ** 6 / 720);
    const lng = ZONE_32_CENTRAL_MERIDIAN + (d
        - (1 + 2 * t1 + c1) * d ** 3 / 6
        + (5 - 2 * c1 + 28 * t1 - 3 * c1 ** 2 + 8 * ep2 + 24 * t1 ** 2) * d ** 5 / 120) / cosPhi1;
    return [lng * 180 / Math.PI, lat * 180 / Math.PI];
}
/**
 * Rewrites every coordinate of a FeatureCollection into WGS84.
 *
 * Elevation (the optional third value) is carried through untouched — it is not
 * affected by the horizontal projection.
 */
function reprojectGeoJson(collection, from) {
    var _a;
    if (from === 'EPSG:4326' || !collection)
        return collection;
    const convert = (coords) => {
        if (typeof (coords === null || coords === void 0 ? void 0 : coords[0]) === 'number' && typeof (coords === null || coords === void 0 ? void 0 : coords[1]) === 'number') {
            const [lng, lat] = utm32ToWgs84(coords[0], coords[1]);
            return coords.length > 2 ? [lng, lat, coords[2]] : [lng, lat];
        }
        return Array.isArray(coords) ? coords.map(convert) : coords;
    };
    const convertGeometry = (geometry) => {
        var _a;
        if (!geometry)
            return geometry;
        if (geometry.type === 'GeometryCollection') {
            return Object.assign(Object.assign({}, geometry), { geometries: ((_a = geometry.geometries) !== null && _a !== void 0 ? _a : []).map(convertGeometry) });
        }
        return Object.assign(Object.assign({}, geometry), { coordinates: convert(geometry.coordinates) });
    };
    return Object.assign(Object.assign({}, collection), { features: ((_a = collection.features) !== null && _a !== void 0 ? _a : []).map(f => (Object.assign(Object.assign({}, f), { geometry: convertGeometry(f.geometry) }))) });
}
/**
 * Best guess at a file's CRS.
 *
 * Prefers the legacy `crs` member when present; otherwise falls back to the
 * coordinate magnitudes — a longitude beyond ±180 or latitude beyond ±90 cannot
 * be degrees, and UTM eastings/northings are always far larger. The dialog shows
 * the result as a preselection the user can override.
 */
function detectImportCrs(collection) {
    var _a, _b, _c, _d;
    const declared = (_c = (_b = (_a = collection === null || collection === void 0 ? void 0 : collection.crs) === null || _a === void 0 ? void 0 : _a.properties) === null || _b === void 0 ? void 0 : _b.name) !== null && _c !== void 0 ? _c : '';
    if (declared.includes('25832'))
        return 'EPSG:25832';
    if (/EPSG:*4326|CRS84/i.test(declared))
        return 'EPSG:4326';
    let looksProjected = false;
    const inspect = (coords) => {
        if (looksProjected)
            return;
        if (typeof (coords === null || coords === void 0 ? void 0 : coords[0]) === 'number' && typeof (coords === null || coords === void 0 ? void 0 : coords[1]) === 'number') {
            if (Math.abs(coords[0]) > 180 || Math.abs(coords[1]) > 90)
                looksProjected = true;
            return;
        }
        if (Array.isArray(coords))
            coords.forEach(inspect);
    };
    ((_d = collection === null || collection === void 0 ? void 0 : collection.features) !== null && _d !== void 0 ? _d : []).forEach((f) => {
        var _a, _b, _c;
        if (((_a = f === null || f === void 0 ? void 0 : f.geometry) === null || _a === void 0 ? void 0 : _a.type) === 'GeometryCollection') {
            ((_b = f.geometry.geometries) !== null && _b !== void 0 ? _b : []).forEach((g) => inspect(g === null || g === void 0 ? void 0 : g.coordinates));
        }
        else {
            inspect((_c = f === null || f === void 0 ? void 0 : f.geometry) === null || _c === void 0 ? void 0 : _c.coordinates);
        }
    });
    return looksProjected ? 'EPSG:25832' : 'EPSG:4326';
}
